// Run a best-effort background write AFTER the browser has finished the work the
// customer can see — without ever dropping the write.
//
// WHY THIS EXISTS — Phase 5, 2026-08-12
//   Two writes sat in the dashboard's first request wave and nothing on screen
//   waited for either: POST /api/audit/log (1,176-1,629 ms observed) and the
//   PUT /routers/last-selected write-back (1,545 ms observed). Both were already
//   fire-and-forget in the sense that no code awaited them — but "not awaited"
//   is not the same as "not competing". They still occupied a connection and
//   bandwidth during boot, on a link where a round trip costs ~262 ms.
//
//   Deferring a write is only safe if it still happens. A naive setTimeout loses
//   the write whenever the customer navigates or closes the tab first, which for
//   an AUDIT record is the one case you most want recorded. So every deferred
//   task is also flushed synchronously on pagehide/visibilitychange, and callers
//   are expected to use keepalive so a send already in flight survives unload.

const queue = [];
let scheduled = false;
let listening = false;

const IDLE_TIMEOUT_MS = 2500;   // ceiling for requestIdleCallback
const FALLBACK_MS     = 1200;   // browsers without requestIdleCallback

function drain() {
  scheduled = false;
  // Copy first: a task that queues another task must not extend this pass.
  const tasks = queue.splice(0, queue.length);
  for (const task of tasks) {
    try { task(); } catch { /* a background write must never break the page */ }
  }
}

function listenForUnload() {
  if (listening || typeof window === "undefined") return;
  listening = true;
  // pagehide covers bfcache and mobile tab kills, which "unload" does not.
  window.addEventListener("pagehide", drain);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") drain();
  });
}

/**
 * Queue a side-effect to run once the browser is idle, or immediately if the
 * page is being hidden or torn down first.
 */
export function deferIdle(task) {
  if (typeof task !== "function") return;
  if (typeof window === "undefined") { try { task(); } catch { /* noop */ } return; }

  queue.push(task);
  listenForUnload();
  if (scheduled) return;
  scheduled = true;

  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(drain, { timeout: IDLE_TIMEOUT_MS });
  } else {
    setTimeout(drain, FALLBACK_MS);
  }
}

/** Flush everything now. Exported for tests and for explicit teardown paths. */
export function flushDeferred() { drain(); }

/** Test seam: how many writes are still waiting. */
export function pendingDeferred() { return queue.length; }
